import {setUiReadinessHandoffObserver} from '@github-ui/ahp-session-client/ui-readiness-handoff'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'

import {createBrowserUiReadinessTracker} from './ui-readiness'
import {
  UI_READINESS_ENTRY_KINDS,
  UI_READINESS_EVENT,
  UI_READINESS_REPOSITORY_SCOPES,
  UI_READINESS_SURFACES,
  type UiReadinessBinding,
  type UiReadinessControls,
  type UiReadinessDependency,
  type UiReadinessFailure,
  type UiReadinessOpen,
  type UiReadinessRole,
  type UiReadinessStateSource,
  type UiReadinessTime,
} from './ui-readiness-contract'
import {readUiReadinessOrigin} from './ui-readiness-origin'

export interface CopilotUiReadinessView {
  readonly ready: boolean
  readonly active: boolean
  readonly open: UiReadinessOpen
  observeOrigin(embeddedData: unknown): void
  readonly viewId: string
  readonly startedAt: UiReadinessTime
  readonly configurationTime: UiReadinessTime | undefined
  readonly configurationPending: boolean
  configurationAvailable(time?: UiReadinessTime, bootstrapComplete?: boolean): void
  waitForConfiguration(): void
  subscribeConfiguration(listener: () => void): () => void
  bind(binding: UiReadinessBinding): void
  initialized(time?: UiReadinessTime): void
  stateAvailable(
    binding: UiReadinessBinding,
    role: Exclude<UiReadinessRole, 'unknown'>,
    source: UiReadinessStateSource,
    time: UiReadinessTime,
  ): string | undefined
  controlsObserved(controls: UiReadinessControls, stateEventId: string): void
  controlsUnavailable(): void
  observationUnavailable(reason: 'visibility_unsupported' | 'visibility_failed'): void
  dependencyChanged(dependency: UiReadinessDependency, waiting: boolean, time?: UiReadinessTime): void
  failure(reason: UiReadinessFailure, time?: UiReadinessTime): void
  dispose(reason?: 'closed' | 'replaced'): void
}

type ReadinessContext = Omit<UiReadinessOpen, 'clientOpenId' | 'origin'>
type ActiveView = {view: CopilotUiReadinessView; pageExit: () => void; visibilityChanged: () => void}
type NavigationIntent = {context: ReadinessContext; pathname: string; started: boolean}

const activeViews = new Map<string, ActiveView>()
const eventViews = new WeakMap<Event, CopilotUiReadinessView>()
const pendingNavigations = new Map<string, CopilotUiReadinessView>()
let navigationIntent: NavigationIntent | undefined
let documentClockId: string | undefined
let documentSequence = 0
let handoffObserverInstalled = false
let originListenerInstalled = false

function reportReadinessError(error: Error) {
  try {
    reportError(error, {critical: false, reactAppName: 'copilot-ui-readiness'})
  } catch {
    // Error reporting must not make telemetry part of the product's control flow.
    // eslint-disable-next-line no-console
    console.error('Unable to report UI readiness telemetry error')
  }
}

export function isUiReadinessEnabled(): boolean {
  try {
    return isFeatureEnabled('copilot_ui_readiness_telemetry')
  } catch {
    reportReadinessError(new Error('Unable to evaluate UI readiness telemetry flag'))
    return false
  }
}

function observe<T>(callback: () => T): T | undefined {
  try {
    return callback()
  } catch {
    reportReadinessError(new Error('Unable to observe UI readiness'))
    return undefined
  }
}

function now(): UiReadinessTime {
  documentClockId ??= crypto.randomUUID()
  return {clockId: documentClockId, observedAtMs: Date.now(), monotonicMs: performance.now()}
}

export function captureUiReadinessTime(): UiReadinessTime | undefined {
  if (!isUiReadinessEnabled()) return undefined
  return observe(now)
}

export function localUiReadinessTime(receipt: {
  observedAtMs: number
  monotonicMs: number
}): UiReadinessTime | undefined {
  if (!isUiReadinessEnabled()) return undefined
  return observe(() => {
    documentClockId ??= crypto.randomUUID()
    return {...receipt, clockId: documentClockId}
  })
}

function visibility() {
  return document.visibilityState === 'visible'
    ? 'visible'
    : document.visibilityState === 'hidden'
      ? 'hidden'
      : 'unknown'
}

function onVisibilityChange() {
  for (const active of activeViews.values()) active.visibilityChanged()
}

function onPageExit() {
  for (const active of activeViews.values()) active.pageExit()
}

function navigationContext(url: URL): ReadinessContext | undefined {
  if (url.origin !== window.location.origin) return undefined
  const chat = /^\/copilot(?:\/agents)?\/(chats|c)\/([^/]+)\/?$/.exec(url.pathname)
  if (chat) {
    return {
      surface: 'chat_app',
      entryKind: chat[1] === 'chats' && chat[2] === 'new' ? 'new' : 'active',
      repositoryScope: 'unknown',
      ...(chat[1] === 'chats' && chat[2] !== 'new' ? {expectedTaskId: chat[2]} : {}),
    }
  }
  if (/^\/copilot(?:\/agents)?(?:\/tasks\/[^/]+)?\/?$/.test(url.pathname)) {
    const task = url.pathname.includes('/tasks/')
    return {
      surface: task ? 'global_task' : 'agents_page',
      entryKind: task ? 'active' : 'new',
      repositoryScope: 'unknown',
      ...(task ? {expectedTaskId: url.pathname.replace(/\/$/, '').split('/').at(-1)} : {}),
    }
  }
  if (/^\/[^/]+\/[^/]+\/(?:agents|tasks\/[^/]+)\/?$/.test(url.pathname)) {
    const task = url.pathname.includes('/tasks/')
    return {
      surface: task ? 'repository_task' : 'repository_agents',
      entryKind: task ? 'active' : 'new',
      repositoryScope: 'repository',
      ...(task ? {expectedTaskId: url.pathname.replace(/\/$/, '').split('/').at(-1)} : {}),
    }
  }
}

function onNavigationIntent(event: MouseEvent) {
  if (!isUiReadinessEnabled()) return
  // Native modified clicks belong to the new document's origin population.
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
  const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null
  if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return
  const url = new URL(link.href, window.location.href)
  const context = navigationContext(url)
  if (!context || url.pathname === window.location.pathname) return
  const intent = {context, pathname: url.pathname, started: false}
  navigationIntent = intent
  setTimeout(() => {
    if (navigationIntent === intent && !intent.started) navigationIntent = undefined
  })
}

function onNavigationStarted() {
  if (navigationIntent) navigationIntent.started = true
}

function onNavigationCommitted() {
  const intent = navigationIntent
  navigationIntent = undefined
  if (!intent?.started || intent.pathname !== window.location.pathname) return
  const view = beginCopilotUiReadiness(intent.context)
  if (!view) return
  // The shell is already initialized. Destination-specific prerequisites still
  // have to be observed before the new composer's controls can become ready.
  view.configurationAvailable(view.startedAt, true)
  pendingNavigations.get(intent.pathname)?.dispose('replaced')
  pendingNavigations.set(intent.pathname, view)
}

function installNavigationListeners() {
  document.addEventListener('click', onNavigationIntent, true)
  document.addEventListener(SOFT_NAV_STATE.START, onNavigationStarted)
  document.addEventListener(SOFT_NAV_STATE.ERROR, () => {
    navigationIntent = undefined
  })
  // eslint-disable-next-line no-restricted-properties
  const pushState = window.history.pushState
  // Capture after the URL commits but before the destination custom element connects.
  // eslint-disable-next-line no-restricted-properties
  window.history.pushState = function (...args) {
    // eslint-disable-next-line no-restricted-properties
    pushState.apply(window.history, args)
    onNavigationCommitted()
  }
}

function installListeners() {
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', onPageExit)
}

function removeListeners() {
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('pagehide', onPageExit)
}

function createView(open: UiReadinessOpen, emitOpen: boolean, startedAt = now()): CopilotUiReadinessView {
  let currentOpen = open
  const tracker = createBrowserUiReadinessTracker(
    {
      now,
      nextSequence: () => documentSequence++,
      visibility,
      emit: fact => sendEvent(UI_READINESS_EVENT, {...fact}, {privacy: 'content_free'}),
      reportError: reportReadinessError,
    },
    open,
  )
  if (emitOpen) tracker.opened()
  const viewId = crypto.randomUUID()
  tracker.startView(viewId, undefined, startedAt)
  let disposed = false
  let reportedObservationError: 'visibility_unsupported' | 'visibility_failed' | undefined
  let configurationTime: UiReadinessTime | undefined
  let configurationPending = true
  const configurationListeners = new Set<() => void>()
  const view: CopilotUiReadinessView = {
    get ready() {
      return tracker.isReady(viewId)
    },
    get active() {
      return !disposed
    },
    get open() {
      return currentOpen
    },
    observeOrigin: embeddedData => {
      observe(() => {
        const origin = readUiReadinessOrigin(embeddedData)
        if (!origin || currentOpen.originRequestId) return
        currentOpen = {...currentOpen, originRequestId: origin.origin_request_id}
        tracker.setOriginRequestId(origin.origin_request_id)
      })
    },
    viewId,
    startedAt,
    get configurationTime() {
      return configurationTime
    },
    get configurationPending() {
      return configurationPending
    },
    configurationAvailable: (time, bootstrapComplete = false) => {
      if (disposed || view.ready) return
      observe(() => {
        const available = time ?? now()
        configurationTime =
          available.clockId === startedAt.clockId && available.monotonicMs < startedAt.monotonicMs
            ? startedAt
            : available
        // A shared model/environment response cannot complete an outstanding host payload.
        if (bootstrapComplete) configurationPending = false
        for (const listener of configurationListeners) listener()
      })
    },
    waitForConfiguration: () => {
      if (!disposed && !view.ready) configurationPending = true
    },
    subscribeConfiguration: listener => {
      configurationListeners.add(listener)
      return () => configurationListeners.delete(listener)
    },
    bind: binding => {
      observe(() => tracker.bindView(viewId, binding))
    },
    initialized: time => {
      observe(() => tracker.initializationCompleted(viewId, time))
    },
    stateAvailable: (binding, role, source, time) =>
      observe(() => tracker.stateAvailable(viewId, {binding, role, source, time})),
    controlsObserved: (controls, stateEventId) => {
      observe(() => tracker.controlsObserved(viewId, controls, stateEventId))
    },
    controlsUnavailable: () => {
      observe(() => tracker.controlsUnavailable(viewId))
    },
    observationUnavailable: reason => {
      observe(() => tracker.controlsUnavailable(viewId))
      if (reportedObservationError === reason) return
      reportedObservationError = reason
      reportReadinessError(new Error(`UI readiness observation unavailable: ${reason}`))
    },
    dependencyChanged: (dependency, waiting, time) => {
      observe(() => tracker.dependencyChanged(viewId, dependency, waiting, time))
    },
    failure: (reason, time) => {
      observe(() => tracker.failure(viewId, reason, time))
    },
    dispose: (reason = 'closed') => {
      if (disposed) return
      disposed = true
      observe(() => tracker.disposeView(viewId, reason))
      activeViews.delete(viewId)
      configurationListeners.clear()
      for (const [path, pending] of pendingNavigations) {
        if (pending === view) pendingNavigations.delete(path)
      }

      if (activeViews.size === 0) removeListeners()
    },
  }
  if (activeViews.size === 0) installListeners()
  activeViews.set(viewId, {
    view,
    pageExit: () => {
      observe(() => tracker.pageExit())
    },
    visibilityChanged: () => {
      observe(() => tracker.visibilityChanged())
    },
  })
  return view
}

export function failPendingCopilotUiInitialization(surface: UiReadinessOpen['surface'], reason: UiReadinessFailure) {
  if (!isUiReadinessEnabled()) return
  for (const {view} of activeViews.values()) {
    if (view.open.surface === surface && !view.ready) view.failure(reason)
  }
}

export function beginCopilotUiReadiness(
  context: ReadinessContext,
  origin?: UiReadinessTime,
): CopilotUiReadinessView | undefined {
  if (!isUiReadinessEnabled()) return undefined
  return observe(() =>
    createView({...context, clientOpenId: crypto.randomUUID(), origin: origin ?? now()}, true, origin),
  )
}

export function adoptCopilotUiReadiness(
  open: UiReadinessOpen,
  startedAt?: UiReadinessTime,
): CopilotUiReadinessView | undefined {
  if (!isUiReadinessEnabled()) return undefined
  return observe(() => createView(open, false, startedAt))
}

export function captureCopilotUiOpen(event: Event, context: ReadinessContext): CopilotUiReadinessView | undefined {
  if (!isUiReadinessEnabled()) return undefined
  const existing = eventViews.get(event)
  if (existing) return existing
  const view = beginCopilotUiReadiness(context)
  if (view) eventViews.set(event, view)
  return view
}

export function claimCopilotUiNavigation(): CopilotUiReadinessView | undefined {
  if (!isUiReadinessEnabled()) return undefined
  const view = pendingNavigations.get(window.location.pathname)
  if (view) pendingNavigations.delete(window.location.pathname)
  return view
}

export function startCopilotRootReadiness(
  name: string,
  previous?: UiReadinessOpen,
  connectedAt?: UiReadinessTime,
): CopilotUiReadinessView | undefined {
  if (!isUiReadinessEnabled()) return undefined
  installHandoffObserver()
  if (!originListenerInstalled) {
    originListenerInstalled = true
    installNavigationListeners()
  }
  if (!['copilot-agents', 'repo', 'repo-agents', 'copilot-chat-app', 'copilot-chat-app-embedded'].includes(name))
    return undefined
  // The original shell already owns this open. Its stored seed is adopted by the
  // existing entry claim, even when the destination bundle never reaches that claim.
  if (new URLSearchParams(window.location.search).has('one_copilot')) return undefined
  const pending = claimCopilotUiNavigation()
  if (pending) return pending
  if (previous) return adoptCopilotUiReadiness(previous, connectedAt)
  const context = navigationContext(new URL(window.location.href, window.location.origin))
  if (context) return beginCopilotUiReadiness(context, connectedAt)
  if (name === 'copilot-chat-app') {
    return beginCopilotUiReadiness({surface: 'chat_app', entryKind: 'new', repositoryScope: 'unknown'}, connectedAt)
  }
  return undefined
}

function installHandoffObserver() {
  if (handoffObserverInstalled) return
  handoffObserverInstalled = true
  setUiReadinessHandoffObserver({
    opened: source => {
      const surface = UI_READINESS_SURFACES.find(candidate => candidate === source) ?? 'unknown'
      const view = beginCopilotUiReadiness({surface, entryKind: 'handoff', repositoryScope: 'unknown'})
      const origin = view?.open.origin
      if (!view || !origin) return undefined
      return {
        version: 1,
        clientOpenId: view.open.clientOpenId,
        clockId: origin.clockId,
        observedAtMs: origin.observedAtMs,
        monotonicMs: origin.monotonicMs,
        surface,
        entryKind: 'handoff',
        repositoryScope: 'unknown',
      }
    },
    claimed: seed => {
      const surface = UI_READINESS_SURFACES.find(candidate => candidate === seed.surface)
      const entryKind = UI_READINESS_ENTRY_KINDS.find(candidate => candidate === seed.entryKind)
      const repositoryScope = UI_READINESS_REPOSITORY_SCOPES.find(candidate => candidate === seed.repositoryScope)
      if (!surface || !entryKind || !repositoryScope) {
        reportReadinessError(new Error('Unsupported UI readiness handoff attribution'))
        return
      }
      for (const {view} of activeViews.values()) {
        if (view.open.clientOpenId === seed.clientOpenId) view.dispose('replaced')
      }
      const view = adoptCopilotUiReadiness({
        clientOpenId: seed.clientOpenId,
        surface,
        entryKind,
        repositoryScope,
        origin: {clockId: seed.clockId, observedAtMs: seed.observedAtMs, monotonicMs: seed.monotonicMs},
      })
      if (!view) return
      view.configurationAvailable(view.startedAt, true)
      pendingNavigations.get(window.location.pathname)?.dispose('replaced')
      pendingNavigations.set(window.location.pathname, view)
    },
    failed: seed => {
      for (const {view} of activeViews.values()) {
        if (view.open.clientOpenId === seed.clientOpenId) {
          view.failure('handoff')
          view.dispose()
        }
      }
    },
    reportError: () => reportReadinessError(new Error('Unable to observe UI readiness handoff')),
  })
}
